import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAuthActions, useAuthDefaultData, useAuthModal } from 'authOld/hooks';
import { mapSetUpAccountForm } from 'authOld/mappers';
import { ISetUpAccountForm, ITermsAndPrivacy } from 'authOld/types';
import { HeaderModalOptions } from 'coreOld/constants';
import { ApiStatuses, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';

import SetUpAccountFormView from './SetUpAccountFormView';

interface IProps {
  onNext: (step: HeaderModalOptions) => void;
}

const SetUpAccountForm = ({ onNext }: IProps) => {
  const [termsAndPrivacyChecked, setTermsAndPrivacyChecked] = useState<ITermsAndPrivacy>({});
  const [email, setEmail] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();

  const { openedAuthModal, closeAuthModal } = useAuthModal();
  const isOpen = openedAuthModal === HeaderModalOptions.SET_UP_ACCOUNT;

  const { t } = useTranslation();
  const { clearNotification, showNotification } = useNotification();
  const { authDefaultData, isLoading, isFetched } = useAuthDefaultData(isOpen);
  const { setUpAccount } = useAuthActions();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const defaultEmail = searchParams.get('email');
    if (defaultEmail) {
      setEmail(defaultEmail);
    }

    searchParams.delete('email');
    setSearchParams(searchParams);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isLoading) {
      showNotification({
        message: t('actionProcessInProgress'),
        type: ApiStatuses.LOADING,
        position: NotificationPosition.MODAL,
      });
    }

    if (isFetched) {
      clearNotification();
    }
  }, [isLoading, isFetched, isOpen]);

  const onSubmit = () => async (data: ISetUpAccountForm) => {
    const { success } = await setUpAccount(mapSetUpAccountForm(data, termsAndPrivacyChecked));

    if (success) {
      onNext(HeaderModalOptions.SET_UP_ACCOUNT);
    }
  };

  const handleOnCheckTermsAndPrivacy = (name: keyof ITermsAndPrivacy) => {
    setTermsAndPrivacyChecked((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  return (
    <SetUpAccountFormView
      isOpen={isOpen}
      onClose={closeAuthModal}
      onSubmit={onSubmit}
      authDefaultData={{
        ...authDefaultData,
        email: authDefaultData?.email || email,
      }}
      isAuthDefaultDataLoading={isLoading}
      termsAndPrivacyChecked={termsAndPrivacyChecked}
      onChange={handleOnCheckTermsAndPrivacy}
    />
  );
};

export default SetUpAccountForm;
